import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface UserPreferences {
  id?: string;
  user_id?: string;
  email_notifications: boolean;
  reminder_notifications: boolean;
  emergency_alerts: boolean;
  weekly_health_summary: boolean;
  data_processing_consent: boolean;
  share_with_healthcare: boolean;
  auto_backup: boolean;
  dark_mode: boolean;
  language: string;
  timezone: string;
  daily_reminder_time: string;
}

const defaultPreferences: UserPreferences = {
  email_notifications: true,
  reminder_notifications: true,
  emergency_alerts: true,
  weekly_health_summary: true,
  data_processing_consent: true,
  share_with_healthcare: false,
  auto_backup: true,
  dark_mode: false,
  language: 'en',
  timezone: 'UTC',
  daily_reminder_time: '09:00:00',
};

export const useUserPreferences = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchPreferences();
    }
  }, [user]);

  const fetchPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('Error fetching preferences:', error);
        return;
      }

      if (data) {
        setPreferences(data);
      } else {
        // Create default preferences for new user
        await createDefaultPreferences();
      }
    } catch (error) {
      console.error('Error in fetchPreferences:', error);
    }
  };

  const createDefaultPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .insert({ ...defaultPreferences, user_id: user.id })
        .select()
        .single();

      if (error) {
        console.error('Error creating preferences:', error);
        return;
      }

      if (data) {
        setPreferences(data);
      }
    } catch (error) {
      console.error('Error creating default preferences:', error);
    }
  };

  const updatePreferences = async (updates: Partial<UserPreferences>) => {
    if (!user) return;

    setIsLoading(true);
    try {
      const newPreferences = { ...preferences, ...updates };
      
      const { data, error } = await supabase
        .from('user_preferences')
        .upsert({
          ...newPreferences,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) {
        console.error('Error updating preferences:', error);
        toast({
          title: "Error",
          description: "Failed to update preferences. Please try again.",
          variant: "destructive"
        });
        return;
      }

      if (data) {
        setPreferences(data);
        toast({
          title: "Preferences updated",
          description: "Your settings have been saved successfully."
        });
      }
    } catch (error) {
      console.error('Error in updatePreferences:', error);
      toast({
        title: "Error",
        description: "Failed to update preferences. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    preferences,
    updatePreferences,
    isLoading,
    fetchPreferences,
  };
};